import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";

/**
 * Gemeinsame Optik der Kartenlisten in der Immobilien-Erfassung
 * (Schäden, Sanierungen, Kunstobjekte, Anbauten): je Eintrag eine Karte mit
 * Nummer, Titel und „×", darunter ein Raster aus Feldern, unten
 * „+ … hinzufügen". Bis 10/2026 stand das allein in `SchadenListe`.
 */
export const useListenStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", rowGap: "12px" },
  intro: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.muted,
  },
  card: {
    backgroundColor: BRAND.white,
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  head: { display: "flex", alignItems: "center", columnGap: "10px" },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "26px",
    height: "26px",
    fontFamily: BRAND.fontAccent,
    fontSize: "0.85rem",
    fontWeight: 600,
    color: BRAND.white,
    backgroundColor: BRAND.purple,
    flexShrink: 0,
    ...shorthands.borderRadius("999px"),
  },
  title: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    color: BRAND.ink,
    flexGrow: 1,
  },
  removeBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: BRAND.muted,
    fontFamily: BRAND.fontPrimary,
    fontSize: "1rem",
    fontWeight: 700,
    width: "26px",
    height: "26px",
    ...shorthands.border("0"),
    ...shorthands.borderRadius("999px"),
    ":hover": { backgroundColor: "#f5eef6", color: BRAND.purple },
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "10px",
    "@media (min-width: 640px)": { gridTemplateColumns: "1fr 1fr 1fr" },
  },
  field: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
    minWidth: 0,
  },
  fullSpan: {
    "@media (min-width: 640px)": { gridColumn: "1 / -1" },
  },
  label: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("4px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    transitionProperty: "border-color",
    transitionDuration: "120ms",
    ":focus": { ...shorthands.borderColor(BRAND.purple) },
  },
  addBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.bg,
    backgroundImage: "none",
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    fontWeight: 700,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "dashed", BRAND.purple),
    ...shorthands.borderRadius("6px"),
    alignSelf: "flex-start",
    ":hover": { backgroundColor: "#f5eef6" },
  },
  empty: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    fontStyle: "italic",
    color: BRAND.muted,
  },
});

/** Stabile Zeilen-ID für Listen-Einträge (Schlüssel und Upload-Zuordnung). */
export const neueListenId = (praefix: string): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${praefix}-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

export const istObjekt = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** Nicht-leerer String oder `undefined`; Zahlen werden zu Text. */
export const textOderNichts = (v: unknown): string | undefined =>
  typeof v === "string" && v !== ""
    ? v
    : typeof v === "number" && Number.isFinite(v)
      ? String(v)
      : undefined;
